import React from 'react';
import PropTypes from 'prop-types';

const formatRands = (cents) => {
  const rands = typeof cents === 'number' ? cents / 100 : 0;
  return `R ${rands.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatEpoch = (epochMs) => {
  if (!epochMs) return 'N/A';
  return new Date(epochMs).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getCents = (value) => (typeof value === 'number' ? value : 0);

const getAllocationValue = (allocation, camelName, legacyName) => (
  allocation?.[camelName] ?? allocation?.[legacyName]
);

const LevyCollectionCard = ({ levy, allocations = [], loading = false, onRefresh }) => {
  if (loading) {
    return (
      <div className="card bg-beige-40 shadow-md rounded-lg p-6 mt-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Arrear Levy Collection</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-2">Loading arrear levy collection data...</span>
        </div>
      </div>
    );
  }

  if (!levy || levy.status !== 'ACTIVE') {
    return null;
  }

  const originalAmount = getCents(levy.originalAmountCents ?? levy.originalAmount);
  const remainingAmount = getCents(levy.remainingAmountCents ?? levy.remainingAmount);
  const recoveredAmount = Math.max(originalAmount - remainingAmount, 0);

  return (
    <div className="card bg-beige-40 shadow-md rounded-lg p-6 mt-6 mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Arrear Levy Collection</h2>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        )}
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-3 mb-4 rounded">
        <p className="font-semibold">Arrear Levy Collection Active</p>
        <p className="mt-1 text-sm">
          An arrear levy collection fee has been implemented on your unit.{' '}
          {levy.recoveryPercentage}% of eligible prepaid top-ups will be allocated
          to levy recovery until the outstanding levy balance is settled.
        </p>
        <p className="mt-2 text-sm">
          This arrear levy collection was implemented by the trustees of the Body
          Corporate. Amounts collected will be allocated to the levy account by
          the trustees or managing agent. For more information, please contact
          your managing agent.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div>
          <p className="text-xs font-semibold uppercase text-gray-500">Original Arrear Levy</p>
          <p className="text-lg font-semibold text-gray-900">{formatRands(originalAmount)}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-gray-500">Recovered</p>
          <p className="text-lg font-semibold text-gray-900">{formatRands(recoveredAmount)}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-gray-500">Remaining</p>
          <p className="text-lg font-semibold text-gray-900">{formatRands(remainingAmount)}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-gray-500">Recovery Rate</p>
          <p className="text-lg font-semibold text-gray-900">{levy.recoveryPercentage}%</p>
        </div>
      </div>

      {allocations.length === 0 ? (
        <p className="text-gray-500">No levy recoveries have been recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Reference
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Top-up
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Levy Recovered
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net Credit
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remaining
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {allocations.map((allocation) => {
                const allocationUuid = allocation.allocationUuid || allocation.uuid;
                const reference = getAllocationValue(
                  allocation,
                  'billingCreditReference',
                  'billing_credit_reference'
                );

                return (
                  <tr key={allocationUuid || reference}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {reference || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatRands(getCents(getAllocationValue(
                        allocation,
                        'grossCreditAmountCents',
                        'grossCreditAmount'
                      )))}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatRands(getCents(getAllocationValue(
                        allocation,
                        'levyRecoveredAmountCents',
                        'levyRecoveredAmount'
                      )))}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatRands(getCents(getAllocationValue(
                        allocation,
                        'netCreditAmountCents',
                        'netCreditAmount'
                      )))}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatRands(getCents(getAllocationValue(
                        allocation,
                        'remainingBalanceAfterCents',
                        'remainingBalanceAfter'
                      )))}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatEpoch(allocation.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

LevyCollectionCard.propTypes = {
  levy: PropTypes.shape({
    levyCollectionUuid: PropTypes.string,
    originalAmountCents: PropTypes.number,
    originalAmount: PropTypes.number,
    remainingAmountCents: PropTypes.number,
    remainingAmount: PropTypes.number,
    recoveryPercentage: PropTypes.number,
    status: PropTypes.string,
    createdAt: PropTypes.number,
  }),
  allocations: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
};

export default LevyCollectionCard;
